<template>
    <div class="map"></div>
</template>

<script>
import { GOOGLE_MAPS_API_KEY } from '@/config/google-maps-config';
export default {
    props: ['marker', 'departure', 'arrival', 'coordinates'],
    data() {
        return {
            resolveInitPromise: null,
            rejectInitPromise: null,
            initialized: true,
            maps_api: [],
            mapsMarker: null,
            markers: [] // Table to store markers
        };
    },
    computed: {},
    methods: {
        initPromise() {
            return new Promise((resolve, reject) => {
                this.resolveInitPromise = resolve;
                this.rejectInitPromise = reject;
            });
        },

        async gmapsInit() {
            const API_KEY = GOOGLE_MAPS_API_KEY;
            const CALLBACK_NAME = 'initMap';
            this.initialized = !!window.google;
            // If Google Maps already is initialized
            // the `initPromise` should be resolved
            // eventually.
            if (this.initialized) {
                /*global google */
                google.maps.event.trigger(window, 'resize', {});
                const geocoder = new google.maps.Geocoder();
                const map = new google.maps.Map(document.getElementsByClassName('map')[0], {
                    zoom: 1,
                    center: { lat: 0, lng: 0 }
                });
                if (this.marker) {
                    const marker = await this.codeAddress(this.marker, geocoder, 'marker');
                    this.mapsMarker = new google.maps.Marker({
                        position: marker.location,
                        map
                    });
                    map.setCenter(marker.location);
                    map.setZoom(10);
                } else {
                    await this.createRoute(google, map, geocoder);
                }
                this.maps_api = [google, geocoder, map];
                return this.initPromise();
            }
            this.initialized = true;
            // The callback function is called by the Google Maps script if it is successfully loaded.
            window[CALLBACK_NAME] = () => this.resolveInitPromise(window.google);
            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.type = 'text/javascript';
            script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&callback=${CALLBACK_NAME}`;
            script.onerror = this.rejectInitPromise;
            script.onload = () => {
                this.gmapsInit();
            };
            document.querySelector('body').appendChild(script);
            return this.initPromise();
        },

        async codeAddress(address, geocoder, type) {
            return new Promise((resolve, reject) => {
                if (!geocoder) {
                    geocoder = this.maps_api[1];
                }
                geocoder.geocode({ address: address }, (results, status) => {
                    if (status !== 'OK' || !results[0]) {
                        return reject(new Error(status));
                    }
                    if (type === 'marker') {
                        resolve(results[0].geometry);
                    } else {
                        resolve(results[0].geometry.location);
                    }
                });
            });
        },

        getFirstAndLastStepPoints(coordinates) {
            const stepPoints = coordinates.filter(coord => 'stepPoint' in coord);
            if (stepPoints.length === 0) return { first: null, last: null };

            return {
                first: stepPoints?.[0],
                last: stepPoints?.[stepPoints?.length - 1]
            };
        },

        generateAddressString(fullAddress) {
            if (!fullAddress) return '';
            const address = fullAddress?.address || {};
            return `${address?.address || ''} ${address?.location || ''} ${address?.countryCode || ''}`.trim();
        },
        checkAddress(fullAddress, addressToCheck) {
            const address = fullAddress?.address || {};
            return addressToCheck.includes(address?.location) && addressToCheck.includes(address?.countryCode);
        },
        async createRoute(google, map, geocoder) {
            if (this.departure && this.arrival) {
                if (google === null || map === null || geocoder === null) {
                    google = this.maps_api[0];
                    geocoder = this.maps_api[1];
                    map = this.maps_api[2];
                }
                // Suppression des anciens marqueurs
                this.markers.forEach(marker => marker.setMap(null));
                this.markers = [];

                const { first, last } = this.getFirstAndLastStepPoints(this.coordinates || []);
                const departureLocation =
                    !this.checkAddress(first, this.departure) && (await this.codeAddress(this.departure, geocoder));
                const arrivalLocation = !this.checkAddress(last, this.arrival) && (await this.codeAddress(this.arrival, geocoder));

                if (this.path !== undefined) this.path.setMap(null);

                const allPoints = [...(this.coordinates || [])];
                departureLocation &&
                    allPoints.unshift({
                        stepPoint: true,
                        address: this.departure,
                        coordinates: departureLocation
                    });
                arrivalLocation &&
                    allPoints.push({
                        stepPoint: true,
                        address: this.arrival,
                        coordinates: arrivalLocation
                    });

                const infoWindow = new google.maps.InfoWindow();

                let points = 0;
                // Adding markers for each point
                allPoints.forEach(point => {
                    let marker = null;
                    if (point?.stepPoint) {
                        points++;
                        const title = this.generateAddressString(point) || point.address;

                        const markerExist = this.markers?.find(pointMarker => pointMarker.title === title);

                        if (!markerExist) {
                            marker = new google.maps.Marker({
                                map,
                                label: points.toString(),
                                title,
                                position: point.coordinates
                            });
                        }
                    } else {
                        marker = new google.maps.Marker({
                            map,
                            position: point.coordinates,
                            icon: {
                                path: google.maps.SymbolPath.CIRCLE,
                                scale: 1,
                                fillColor: '#3200E6',
                                fillOpacity: 1,
                                strokeWeight: 2,
                                strokeColor: '#3200E6',
                                scaledSize: new google.maps.Size(9, 9) // largeur x hauteur en pixels
                            }
                        });
                        //Mouseover on the circle → Show the Marker + Infowindow
                        marker.addListener('mouseover', () => {
                            marker.setMap(map); // show the marker
                            const content = point.time?.replace('T', ' ')?.replace('Z', '');
                            infoWindow.setContent(`<div style="font-size: 12px;">${content}</div>`);
                            infoWindow.open(map, marker);
                        });
                    }

                    this.markers.push(marker);
                });

                this.path = new google.maps.Polyline({
                    path: allPoints.map(point => point.coordinates),
                    geodesic: true,
                    strokeColor: '#4986E7',
                    strokeOpacity: 1.0,
                    strokeWeight: 2
                });
                this.path.setMap(map);

                const bounds = new google.maps.LatLngBounds();
                allPoints.forEach(point => bounds.extend(point.coordinates));
                map.fitBounds(bounds);
            }
        }
    },
    async mounted() {
        try {
            await this.gmapsInit();
            const google = this.maps_api[0];
            const geocoder = this.maps_api[1];
            const map = this.maps_api[2];
            if (this.marker) {
                const marker = await this.codeAddress(this.marker, geocoder);
                this.mapsMarker = new google.maps.Marker({
                    position: marker.location,
                    map
                });
                map.setCenter(marker.location);
            } else {
                await this.createRoute(google, map, geocoder);
            }
        } catch (error) {
            console.error(error);
        }
    },
    watch: {
        async marker() {
            const geocoder = this.maps_api[1];
            const map = this.maps_api[2];
            const marker = await this.codeAddress(this.marker, geocoder, 'marker');
            this.mapsMarker.setMap(null);
            this.mapsMarker = new google.maps.Marker({
                position: marker.location,
                map
            });
            map.setCenter(marker.location);
        },
        async departure() {
            await this.gmapsInit();
        },
        async arrival() {
            await this.gmapsInit();
        },
        async coordinates() {
            await this.gmapsInit();
        }
    }
};
</script>

<style>
.map {
    width: 100%;
    height: 100%;
}
</style>
